import React, { useState, useEffect } from "react";
import "../css/Candidate_dashboard.css";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faList,
  faSignOutAlt,
  faHome,
  faSpinner,
  faProjectDiagram,
  faCheckCircle,
  faInfoCircle,
  faCreditCard
} from "@fortawesome/free-solid-svg-icons";

function CandidateDashboard() {
  const [profilePhoto, setProfilePhoto] = useState("/images/default.png");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState({
    name: "John Doe",
    email: "john@example.com"
  });
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [adminConf, setAdminConf] = useState(false);
  const [allottedProject, setAllottedProject] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [offerPaymentStatus, setOfferPaymentStatus] = useState(null);
  const navigate = useNavigate();

  // Check payment status and admin confirmation on component mount
  useEffect(() => {
    const checkPaymentAndAdminStatus = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setError("No authentication token found");
          navigate("/login");
          return;
        }

        // Check payment status
        const paymentRes = await authFetch("/api/students/me/reg_payment_status", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (paymentRes.ok) {
          const paymentData = await paymentRes.json();
          setPaymentStatus(paymentData.reg_payment_status);
          console.log("Payment status:", paymentData.reg_payment_status);

          // If payment is not paid, redirect to payment page
          if (paymentData.reg_payment_status === "unpaid") {
            navigate("/payment1", {
              state: {
                message: "Please complete your registration payment to access the dashboard",
                paymentType: "REG",
                returnUrl: "/Candidatedashboard"
              }
            });
            return;
          }
          if (paymentData.reg_payment_status === "pending") {
            navigate("/pending");
            return;
          }

          // If payment is paid, continue to load dashboard data
          if (paymentData.reg_payment_status === "paid") {
            await fetchDashboardData();
            await fetchProfilePhoto();
          }
        } else {
          const err = await paymentRes.json();
          throw new Error(err.detail || "Failed to fetch payment status");
        }
      } catch (error) {
        setError(error.message || "Error checking payment status");
        console.error("Payment check error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Function to fetch dashboard data including admin confirmation
    const fetchDashboardData = async () => {
      try {
        // Check admin confirmation status
        const adminConfRes = await authFetch("/api/students/admin_conf");
        if (adminConfRes.ok) {
          const adminConfData = await adminConfRes.json();
          console.log("Admin confirmation status:", adminConfData);
          setAdminConf(adminConfData.admin_conf);

          if (adminConfData.admin_conf) {
            // If admin confirmed, fetch allotted project
            const allottedRes = await authFetch("/api/students/me/allotted_project");
            if (allottedRes.ok) {
              const allottedData = await allottedRes.json();
              console.log("Fetched allotted project:", allottedData);
              setAllottedProject(allottedData);

              // Fetch offer payment status for the allotted project
              const offerPaymentRes = await authFetch("/api/students/me/offer_payment_status");
              if (offerPaymentRes.ok) {
                const offerPaymentData = await offerPaymentRes.json();
                setOfferPaymentStatus(offerPaymentData.offer_payment_status);
              }

            } else if (allottedRes.status === 404) {
              // No project allotted yet
              setMessage({
                text: "Admin has confirmed but no project has been allotted yet. Please check back later.",
                type: "info"
              });
            } else {
              setMessage({
                text: "Error fetching allotted project details.",
                type: "error"
              });
            }
          }
        }

        // Fetch candidate basic info
        const candidateRes = await authFetch("/api/students/me");
        if (candidateRes.ok) {
          const candidateData = await candidateRes.json();
          setUserData({
            name: candidateData.name || userData.name,
            email: candidateData.email || userData.email
          });
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setMessage({
          text: "Failed to load some dashboard data.",
          type: "error"
        });
      }
    };

    // Function to fetch profile photo
    const fetchProfilePhoto = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch("/api/students/profile_photo", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          if (data.profile_photo_path) {
            const cleanedPath = data.profile_photo_path.startsWith("/")
              ? data.profile_photo_path.substring(1)
              : data.profile_photo_path;

            const fullUrl = `${window.location.origin}/${cleanedPath}`;
            console.log("Full URL for profile photo:", fullUrl);
            setProfilePhoto(`${fullUrl}?${Date.now()}`);
          }
        } else if (res.status === 404) {
          setProfilePhoto("/images/default.png");
        } else {
          console.error("Failed to fetch profile photo");
          setProfilePhoto("/images/default.png");
        }
      } catch (error) {
        console.error("Error fetching profile photo:", error);
        setProfilePhoto("/images/default.png");
      }
    };

    checkPaymentAndAdminStatus();

    const handleStorageChange = () => {
      if (localStorage.getItem("profileUpdated")) {
        fetchProfilePhoto();
        localStorage.removeItem("profileUpdated");
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [navigate]);

  // Handle navigation to project payment page (payment2)
  const handleProjectPaymentNavigation = () => {
    navigate("/payment2", {
      state: {
        message: "Complete your project payment",
        paymentType: "PROJECT",
        projectId: allottedProject?.id,
        projectTitle: allottedProject?.title,
        returnUrl: "/Candidatedashboard"
      }
    });
  };

  // Handle navigation based on admin confirmation
  const handleProjectNavigation = () => {
    navigate("/candidatepreferences");
  };

  // Get button text based on admin confirmation
  const getProjectButtonText = () => {
    if (adminConf) {
      return allottedProject ? "View Project Details" : "Check Project Status";
    }
    return "Project Preferences";
  };

  // Get button icon based on admin confirmation
  const getProjectButtonIcon = () => {
    return adminConf ? faProjectDiagram : faList;
  };

  // Show loading state while checking payment
  if (isLoading) {
    return (
      <div className="cd-container">
        <div className="cd-main-content">
          <div className="cd-loading">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            <h2>Loading Dashboard...</h2>
            <p>Please wait while we prepare your dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="cd-container">
        <div className="cd-main-content">
          <div className="cd-error">
            <h2>Error</h2>
            <p>{error}</p>
            <button
              className="cd-btn"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
            <button
              className="cd-btn secondary"
              onClick={() => {
                localStorage.removeItem("access_token");
                navigate("/login");
              }}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Only render dashboard if payment is paid
  if (paymentStatus !== "paid") {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="cd-container">
      <div className="cd-sidebar">
        <div className="cd-profile-wrapper">
          {/* Profile content commented out */}
        </div>

        <button
          className="cd-btn"
          onClick={() => navigate("/candidate_profile")}
        >
          <FontAwesomeIcon icon={faUser} />
          <span>Profile Page</span>
        </button>

        <button
          className={`cd-btn ${adminConf ? 'project-details-btn' : ''}`}
          onClick={handleProjectNavigation}
        >
          <FontAwesomeIcon icon={getProjectButtonIcon()} />
          <span>{getProjectButtonText()}</span>
        </button>

        <button
          className="cd-btn"
          onClick={() => {
            localStorage.removeItem("access_token");
            navigate("/");
          }}
        >
          <FontAwesomeIcon icon={faSignOutAlt} />
          <span>Logout</span>
        </button>
      </div>

      <div className="cd-main-content">
        <div className="cd-payment-badge">
          {/* Payment badge commented out */}
        </div>

        {/* Message display */}
        {message.text && (
          <div className={`cd-message ${message.type}`}>
            <FontAwesomeIcon icon={message.type === 'success' ? faCheckCircle : faInfoCircle} />
            <p>{message.text}</p>
          </div>
        )}

        {/* PAYMENT BUTTON - CENTERED ON SCREEN */}
        {/* This button appears when:
            1. Admin has confirmed (adminConf === true)
            2. Project has been allotted (allottedProject !== null)
            3. Offer payment is unpaid (offerPaymentStatus === "unpaid")
        */}


        <h1>Welcome, {userData.name}!</h1>

        {/* Conditional content based on admin confirmation */}
        {adminConf ? (
          <div className="cd-confirmed-content">
            {/* Admin confirmed content - you can uncomment this to show project details */}
            {allottedProject && (
              <div className="cd-allotted-project">
                <h3>You have been  allotted  to the following project</h3>
                <div className="project-card">
                  <h4>{allottedProject.title}</h4>
                  <p><strong>Description:</strong> {allottedProject.description}</p>
                  <p><strong>Department:</strong> {allottedProject.department?.name || 'N/A'}</p>
                  <p><strong>Professor:</strong> {allottedProject.professor?.name || 'N/A'}</p>
                  <p><strong>Mode:</strong> {allottedProject.mode || 'N/A'}</p>
                  <p><strong>Duration:</strong> {allottedProject.duration || 'N/A'}</p>
                  <p><strong>Payment Status:</strong> {offerPaymentStatus || 'N/A'}</p>

                  {adminConf && allottedProject && offerPaymentStatus === "unpaid" && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '300px',
                      width: '100%'
                    }}>
                      <button
                        onClick={handleProjectPaymentNavigation}
                        style={{
                          backgroundColor: '#28a745',
                          color: 'white',
                          padding: '20px 40px',
                          fontSize: '20px',
                          fontWeight: 'bold',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '15px',
                          boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          animation: 'pulse 2s infinite'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                        }}
                      >
                        <FontAwesomeIcon icon={faCreditCard} size="2x" />
                        <span>Complete Project Payment Now</span>
                      </button>
                    </div>
                  )}
{adminConf && allottedProject && offerPaymentStatus === "pending" && (
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                      <p>
                        <FontAwesomeIcon icon={faSpinner} spin /> Your project payment is currently being processed. Please wait for confirmation.
                      </p>
                    </div>
                  )}

                  {!allottedProject &&(
                    <>
                    After submitting the projects You can drag your preference to change the order
                    </>
                  )

                  }
                  {/* Show offer payment status */}
                  {!offerPaymentStatus && (
                    // <p><strong>Payment Status:</strong> 
                    //   <span style={{ 
                    //     color: offerPaymentStatus === 'paid' ? 'green' : 
                    //            offerPaymentStatus === 'pending' ? 'orange' : 'red',
                    //     fontWeight: 'bold',
                    //     marginLeft: '10px'
                    //   }}>
                    //     {offerPaymentStatus === 'paid' ? '✓ Paid' : 
                    //      offerPaymentStatus === 'pending' ? '⏳ Pending' : '⚠️ Payment Required'}
                    //   </span>
                    // </p>
                    <>

                      <p>
                        Please pay to confirm your project allotment and proceed with the internship process.
                      </p></>

                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="cd-unconfirmed-content">
            {/* Unconfirmed content - can be uncommented if needed */}
            {/* <p>Your account is awaiting admin confirmation.</p> */}
          </div>
        )}
        {/* <p>Please submit your preferences before the dead</p> */}

      

      {
        !allottedProject && !adminConf && (  <>
        <p>Use the sidebar to navigate through different sections.</p>
          <p>
            Deadline to submit preferences : 16 April 2026 02:00 PM
          </p>
          </>
        )
      }
      </div>

      {/* Add CSS animation for the button */}
      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.02);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default CandidateDashboard;